import {
  Box,
  ButtonBase,
  IconButton,
  Chip,
  Stack,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import { Link } from "react-router";
import { ImageFill } from "../../../components/image-fill";
import CheckIcon from "@mui/icons-material/Check";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import { useState, MouseEvent } from "react";

// Helper function to detect if a file is a PDF based on URL/path
const isPdfFile = (url: string | undefined): boolean => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  
  // Check for .pdf extension (with or without query params)
  if (lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf?') || lowerUrl.includes('application/pdf')) {
    return true;
  }
  
  // Check for URL-encoded .pdf in Firebase Storage URLs
  // Firebase encodes slashes as %2F, so "file.pdf" becomes "file.pdf" or "2Ffile.pdf"
  if (lowerUrl.includes('.pdf') || lowerUrl.includes('%2fpdf') || lowerUrl.includes('2f.pdf')) {
    return true;
  }
  
  // Check for common Firebase Storage patterns with .pdf
  if (lowerUrl.includes('firebasestorage') && lowerUrl.includes('pdf')) {
    return true;
  }
  
  return false;
};

export const DesignFileBlock = ({
  image,
  to,
  title,
  isPlaceholder = false,
  lastModified,
  onEdit,
  onToggleFeatured,
  onDelete,
  onSetAsCover,
  onPreview,
  isFeatured = false,
  disabled = false,
  fileType,
  imageCount,
  published,
  onTogglePublish,
}: {
  image?: string;
  to?: string;
  title: string;
  isPlaceholder?: boolean;
  lastModified?: string;
  onEdit?: () => void;
  onToggleFeatured?: () => void;
  onDelete?: () => void;
  onSetAsCover?: () => void;
  onPreview?: () => void;
  isFeatured?: boolean;
  disabled?: boolean;
  fileType?: "project" | "before-after" | "mood-board";
  imageCount?: number;
  published?: boolean;
  onTogglePublish?: () => void;
}) => {
  const trimmedTitle = title?.trim?.() ?? "";
  const displayTitle = trimmedTitle || "Add Project Title Here";
  const titleColor = trimmedTitle ? "rgba(0,0,0,0.87)" : "rgba(0,0,0,0.5)";
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const hasActions = Boolean(
    onEdit || onToggleFeatured || onDelete || onSetAsCover || onTogglePublish
  );
  const addToBioLabel = isFeatured ? "Remove from Bio" : "Add to Bio";
  const showPublishStatus = typeof published === "boolean";
  const publishLabel = published ? "Published" : "Draft";
  const publishChipStyles = published
    ? { backgroundColor: "rgba(46, 125, 50, 0.9)" }
    : { backgroundColor: "rgba(237, 108, 2, 0.9)" };
  const canTogglePublish = showPublishStatus && Boolean(onTogglePublish);
  const publishActionLabel = published ? "Unpublish" : "Publish";

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    if (disabled || !hasActions) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => setMenuAnchor(null);

  const handleEdit = () => {
    handleCloseMenu();
    onEdit?.();
  };

  const handleToggleFeatured = () => {
    handleCloseMenu();
    onToggleFeatured?.();
  };

  const handleDelete = () => {
    handleCloseMenu();
    onDelete?.();
  };

  const handleSetAsCover = () => {
    handleCloseMenu();
    onSetAsCover?.();
  };

  const handleTogglePublish = () => {
    handleCloseMenu();
    onTogglePublish?.();
  };

  if (isPlaceholder) {
    const destination = to ?? "#";
    return (
      <ButtonBase
        component={Link}
        to={destination}
        sx={{
          width: 150,
          height: 228,
          border: "1px dashed #D9D9D9",
          borderRadius: "16px",
          backgroundColor: "#FBFBFB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#F2F2F2",
            borderColor: "#BDBDBD",
          },
        }}
      >
        <Stack spacing={1} alignItems="center">
          <AddIcon sx={{ color: "#BDBDBD", fontSize: 28 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: "#BDBDBD" }}>
            Add
          </Typography>
        </Stack>
      </ButtonBase>
    );
  }

  const handleCardClick = (event: React.MouseEvent) => {
    // Don't trigger preview if disabled or no handler provided
    if (disabled || !onPreview) {
      return;
    }
    // The kebab menu already uses stopPropagation, so clicks on it won't reach here
    // But as a safety check, verify we're not clicking on a button
    const target = event.target as HTMLElement;
    if (target.closest('button')) {
      return;
    }
    onPreview();
  };

  return (
    <Box
      onClick={handleCardClick}
      sx={{
        position: "relative",
        width: 150,
        height: 228,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: "#fff",
        cursor: onPreview && !disabled ? "pointer" : "default",
        transition: "transform 0.2s ease, boxShadow 0.2s ease",
        "&:hover": onPreview && !disabled ? {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        } : {},
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ position: "relative", width: "100%", height: 150 }}>
          {isPdfFile(image) ? (
            // PDF Placeholder - link to open PDF
            <Box
              component="a"
              href={image ?? ""}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "150px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F5F5F5",
                textDecoration: "none",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                },
              }}
            >
              <Stack spacing={1} alignItems="center">
                <PictureAsPdfIcon sx={{ fontSize: 40, color: "#D32F2F" }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PDF
                </Typography>
              </Stack>
            </Box>
          ) : (
            // Image Preview - show actual image
            <ImageFill
              src={image ?? ""}
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "150px",
                backgroundColor: "#F5F5F5",
              }}
            />
          )}

          {/* Top overlay - Featured chip and Kebab menu */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left side - Featured chip */}
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              {isFeatured && (
                <Chip
                  icon={<CheckIcon sx={{ fontSize: 16 }} />}
                  label="Featured"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    fontSize: "12px",
                    height: 24,
                    "& .MuiChip-icon": {
                      color: "#fff",
                    },
                  }}
                />
              )}
              {showPublishStatus && (
                <Chip
                  label={publishLabel}
                  size="small"
                  sx={{
                    ...publishChipStyles,
                    color: "#fff",
                    fontSize: "12px",
                    height: 24,
                  }}
                />
              )}
            </Stack>
            
            {/* Right side - Kebab menu */}
            {hasActions && (
              <IconButton
                size="small"
                disabled={disabled}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "#fff",
                  width: 32,
                  height: 32,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    color: "rgba(255,255,255,0.6)",
                  },
                }}
                onClick={handleOpenMenu}
              >
                <MoreVertIcon 
                  sx={{ 
                    fontSize: 18,
                    transform: "none", // Ensure no rotation
                  }} 
                />
              </IconButton>
            )}
          </Box>

          {/* Bottom left - Image count indicator */}
          {imageCount !== undefined && imageCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                left: 12,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                borderRadius: "12px",
                px: 1,
                py: 0.5,
              }}
            >
              <ImageIcon sx={{ fontSize: 14, color: "#fff" }} />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#fff",
                }}
              >
                {imageCount}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            px: 1,
            pt: 1,
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              mb: 0.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: titleColor,
            }}
            title={displayTitle}
          >
            {displayTitle}
          </Typography>
          {lastModified && (
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              Last modified {lastModified}
            </Typography>
          )}
        </Box>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {onEdit && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
        {canTogglePublish && (
          <MenuItem onClick={handleTogglePublish}>{publishActionLabel}</MenuItem>
        )}
        {/* Only show "Add to Bio" option for Project Files */}
        {onToggleFeatured && fileType === "project" && (
          <MenuItem onClick={handleToggleFeatured}>{addToBioLabel}</MenuItem>
        )}
        {/* Show "Set as Cover Image" option for Before & After files */}
        {onSetAsCover && fileType === "before-after" && (
          <MenuItem onClick={handleSetAsCover}>Set as Cover Image</MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: "#D32F2F" }}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
